/**
 * Redirect helpers — bounce unauthed users to the cerberus auth UI and consume
 * the `#access_token=...&refresh_token=...&user_id=...` hash fragment after
 * the auth UI redirects back.
 */
import { CERBERUS_AUTH_UI_URL } from './config'
import { unsafeDecodeClaims } from './jwt'
import { setTokens, type StoredTokens } from './tokens'

export function loginUrl(returnTo: string, authUiUrl = CERBERUS_AUTH_UI_URL): string {
  const base = authUiUrl.endsWith('/') ? authUiUrl.slice(0, -1) : authUiUrl
  return `${base}/login?return_to=${encodeURIComponent(returnTo)}`
}

/**
 * Internal /login route URL (Etapa 09 substitute for the still-blocked
 * cerberus-auth-ui in Etapa 07). Used by `redirectToLogin` by default.
 */
export function internalLoginUrl(returnTo: string): string {
  return `/login?return_to=${encodeURIComponent(returnTo)}`
}

function shouldUseInternalLogin(): boolean {
  // Default true because Etapa 07 (auth UI) is blocked by hestia. Flip to
  // `true` on env `VITE_CERBERUS_AUTH_UI_REDIRECT=true` to bounce externally.
  const flag = (import.meta.env as Record<string, string | undefined>)[
    'VITE_CERBERUS_AUTH_UI_REDIRECT'
  ]
  return flag !== 'true'
}

/**
 * Routes the user can be on without an auth token (no redirect needed).
 * Mirrors `AuthGate.tsx`'s `PUBLIC_PATHS` — the gate must let any route
 * here render to children, otherwise we get a blank screen (gate
 * rejects render) while this helper refuses to redirect. `/entrar` and
 * `/verify` are here because they run in the gap between "Cerberus
 * account created" and "first authed login"; without the exemption,
 * AuthProvider redirects them to /login before the page can mount and
 * the flow is unreachable.
 */
const PUBLIC_PATHS: ReadonlySet<string> = new Set([
  '/login',
  '/reset',
  '/entrar',
  '/verify',
  // Mobile-web-minimal public surfaces; see __root.tsx.
  '/',
  '/instalar',
])

const PUBLIC_PREFIXES: readonly string[] = ['/share/']

export function redirectToLogin(returnTo?: string): void {
  if (typeof window === 'undefined') return
  const pathname = window.location.pathname
  if (PUBLIC_PATHS.has(pathname)) return
  if (PUBLIC_PREFIXES.some((p) => pathname.startsWith(p))) return
  const target = returnTo ?? window.location.href
  if (shouldUseInternalLogin()) {
    window.location.assign(internalLoginUrl(target))
    return
  }
  window.location.assign(loginUrl(target))
}

function normalizeUserId(value: string | null | undefined): string | null {
  const trimmed = value?.trim()
  if (!trimmed || trimmed === 'undefined' || trimmed === 'null') return null
  return trimmed
}

function userIdFromAccessToken(accessToken: string): string | null {
  const claims = unsafeDecodeClaims(accessToken)
  return normalizeUserId(claims?.sub)
}

/**
 * Parses `#access_token=...&refresh_token=...&user_id=...` from the current
 * URL. Stores tokens, clears the hash from history, returns the parsed tokens
 * (or null when no auth fragment is present).
 *
 * Idempotent + side-effect-safe outside the browser.
 */
export function consumeReturnFromUrl(): StoredTokens | null {
  if (typeof window === 'undefined' || !window.location) return null
  const hash = window.location.hash
  if (!hash || hash.length < 2) return null
  const raw = hash.startsWith('#') ? hash.slice(1) : hash
  const params = new URLSearchParams(raw)
  const access_token = params.get('access_token')
  const refresh_token = params.get('refresh_token')
  const user_id = normalizeUserId(params.get('user_id')) ??
    (access_token ? userIdFromAccessToken(access_token) : null)
  if (!access_token || !refresh_token || !user_id) return null

  const stored: StoredTokens = { access_token, refresh_token, user_id }
  setTokens(stored)

  // Strip hash without reloading.
  try {
    const url = new URL(window.location.href)
    url.hash = ''
    window.history.replaceState(null, '', url.toString())
  } catch {
    /* noop */
  }

  return stored
}
